import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/button";
import { Check, X, Send, Archive, RotateCcw, PenLine } from "lucide-react";
import {
  approveTextSign,
  rejectTextSign,
  forwardTextSign,
  transferInitialTextSign,
  publishTextSign,
  recallTextSign,
  suggestRegisterNumber,
  fetchSigners,
} from "@/features/text-sign/actions-api";
import { useAuthStore } from "@/features/auth/store";
import { SIGN_ROLES } from "@pkhoffice/shared";

type ActionProps = {
  textId: number;
  state: number | null;
  creatorId: number | null;
  /** Luồng ký — để biết người đang xem có phải người ký cấp hiện tại. */
  trail?: { receiverId: number | null; state: number | null; signatureType?: number | null }[];
  onChanged: () => void;
};

export function TextSignActionBar({ textId, state, creatorId, trail, onChanged }: ActionProps) {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const [loading, setLoading] = useState<string | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  const [comment, setComment] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [forwardSearch, setForwardSearch] = useState("");
  const [forwardPick, setForwardPick] = useState<{ staffId: number; fullName: string } | null>(
    null,
  );
  const [signers, setSigners] = useState<Awaited<ReturnType<typeof fetchSigners>>>([]);
  const [regNumber, setRegNumber] = useState("");
  // #4279: số gợi ý (tự sinh) để biết người dùng có sửa tay hay không + lỗi ban hành hiện tại chỗ.
  const [suggestedNumber, setSuggestedNumber] = useState("");
  const [publishError, setPublishError] = useState<string | null>(null);
  const [personalReceivers, setPersonalReceivers] = useState("");

  const [initialOpen, setInitialOpen] = useState(false);
  const [initialSearch, setInitialSearch] = useState("");
  const [initialSigners, setInitialSigners] = useState<Awaited<ReturnType<typeof fetchSigners>>>(
    [],
  );
  const [initialPicks, setInitialPicks] = useState<number[]>([]);
  const [initialComment, setInitialComment] = useState("");

  const hasRole = (roles: string[]) => roles.some((r) => user?.roles?.includes(r));
  const isCreator = user?.staffId === creatorId;

  // #3679 + #4127: dòng luồng ký của chính người xem đang tới lượt (chưa xử lý,
  // hoặc đang chờ nháy xong với người uỷ quyền).
  const myRow = (trail ?? []).find(
    (t) =>
      t.receiverId != null && t.receiverId === user?.staffId && (t.state === 0 || t.state === 5),
  );
  const isPendingSigner = !!myRow;
  const isInitialer = myRow?.signatureType === 2; // người xem đang ở bước ký nháy
  const canApprove = (state === 1 || state === 5) && isPendingSigner;
  // Chỉ người ký DUYỆT đang đúng lượt (chưa uỷ, state dòng = 0) mới được chuyển ký nháy.
  const canTransferInitial = canApprove && !isInitialer && myRow?.state === 0;
  // Từ chối/Uỷ quyền chỉ hợp lệ khi dòng của người xem còn ở state=0 (chưa uỷ ký
  // nháy) — backend reject()/forward() chỉ chấp nhận STATE=0, trong khi approve()
  // chấp nhận cả STATE=5 (đang chờ người được uỷ nháy xong).
  const canRejectForward = canApprove && myRow?.state === 0;
  const canPublish = state === 3 && hasRole(["VT", "ADMIN"]);
  const canRecall = state === 4 && (isCreator || hasRole(["VT", "ADMIN"]));

  async function onApprove() {
    setLoading("approve");
    try {
      await approveTextSign(textId, { signType: "image", comment });
      onChanged();
      qc.invalidateQueries({ queryKey: ["text-sign"] });
      setApproveOpen(false);
      setComment("");
    } finally {
      setLoading(null);
    }
  }

  async function onReject() {
    setLoading("reject");
    try {
      await rejectTextSign(textId, { comment: rejectReason });
      onChanged();
      qc.invalidateQueries({ queryKey: ["text-sign"] });
      setRejectOpen(false);
      setRejectReason("");
    } finally {
      setLoading(null);
    }
  }

  async function onForward() {
    if (!forwardPick) return;
    setLoading("forward");
    try {
      await forwardTextSign(textId, {
        toStaffId: forwardPick.staffId,
        toRole: SIGN_ROLES.LDDV,
        comment,
      });
      onChanged();
      qc.invalidateQueries({ queryKey: ["text-sign"] });
      setForwardOpen(false);
      setForwardPick(null);
      setComment("");
    } finally {
      setLoading(null);
    }
  }

  async function onPublish() {
    setLoading("publish");
    setPublishError(null);
    try {
      const staffIds = personalReceivers
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      const typed = regNumber.trim();
      // #4279: gửi số CHỈ khi văn thư sửa khác số gợi ý; để nguyên/để trống → BE tự
      // cấp số atomic (không bị đụng khi 2 văn thư ban hành cùng lúc).
      const registerNumber = typed && typed !== suggestedNumber ? typed : undefined;
      await publishTextSign(textId, {
        registerNumber,
        personalReceivers: staffIds,
        orgReceivers: [],
      });
      onChanged();
      qc.invalidateQueries({ queryKey: ["text-sign"] });
      setPublishOpen(false);
    } catch (e) {
      // #4279: KHÔNG nuốt lỗi — hiện lỗi ngay trong hộp thoại (vd: số đã tồn tại).
      const ax = e as { response?: { data?: { msg?: string; message?: string } } };
      setPublishError(
        ax.response?.data?.msg ??
          ax.response?.data?.message ??
          "Ban hành thất bại. Vui lòng thử lại.",
      );
    } finally {
      setLoading(null);
    }
  }

  async function onRecall() {
    if (!confirm("Thu hồi văn bản đã ban hành?")) return;
    setLoading("recall");
    try {
      await recallTextSign(textId);
      onChanged();
      qc.invalidateQueries({ queryKey: ["text-sign"] });
    } finally {
      setLoading(null);
    }
  }

  async function loadForwardSigners() {
    const r = await fetchSigners({ role: SIGN_ROLES.LDDV, q: forwardSearch.trim() || undefined });
    setSigners(r);
  }

  async function loadInitialSigners() {
    const r = await fetchSigners({ q: initialSearch.trim() || undefined });
    setInitialSigners(r);
  }

  async function onTransferInitial() {
    if (!initialPicks.length) return;
    setLoading("initial");
    try {
      await transferInitialTextSign(textId, {
        initialerStaffIds: initialPicks,
        comment: initialComment || undefined,
      });
      onChanged();
      qc.invalidateQueries({ queryKey: ["text-sign"] });
      setInitialOpen(false);
      setInitialPicks([]);
      setInitialComment("");
    } finally {
      setLoading(null);
    }
  }

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {canApprove && (
          <Button variant="default" size="md" onClick={() => setApproveOpen(true)}>
            <Check className="h-4 w-4 mr-1.5" /> {isInitialer ? "Ký nháy" : "Ký duyệt"}
          </Button>
        )}
        {canRejectForward && (
          <>
            <Button variant="outline" size="md" onClick={() => setRejectOpen(true)}>
              <X className="h-4 w-4 mr-1.5" /> Từ chối
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={() => {
                setForwardOpen(true);
                setSigners([]);
              }}
            >
              <Send className="h-4 w-4 mr-1.5" /> Uỷ quyền
            </Button>
          </>
        )}
        {canTransferInitial && (
          <Button
            variant="outline"
            size="md"
            onClick={() => {
              setInitialOpen(true);
              setInitialSigners([]);
            }}
          >
            <PenLine className="h-4 w-4 mr-1.5" /> Chuyển ký nháy
          </Button>
        )}
        {canPublish && (
          <Button
            variant="default"
            size="md"
            onClick={async () => {
              // #4279: tự sinh số gợi ý rồi mở hộp thoại (văn thư vẫn sửa được).
              setPublishError(null);
              let sug = "";
              try {
                sug = (await suggestRegisterNumber(textId)).suggested ?? "";
              } catch {
                /* không chặn ban hành nếu peek lỗi — để trống thì BE tự cấp số */
              }
              setSuggestedNumber(sug);
              setRegNumber(sug);
              setPublishOpen(true);
            }}
          >
            <Archive className="h-4 w-4 mr-1.5" /> Ban hành
          </Button>
        )}
        {canRecall && (
          <Button variant="outline" size="md" onClick={onRecall} disabled={loading === "recall"}>
            <RotateCcw className="h-4 w-4 mr-1.5" /> Thu hồi
          </Button>
        )}
      </div>

      <Modal
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        title="Ký duyệt văn bản"
        footer={
          <>
            <Button variant="outline" size="md" onClick={() => setApproveOpen(false)}>
              Huỷ
            </Button>
            <Button
              variant="default"
              size="md"
              onClick={onApprove}
              disabled={loading === "approve"}
            >
              {loading === "approve" ? "Đang ký…" : "Xác nhận ký"}
            </Button>
          </>
        }
      >
        <label className="block text-sm font-medium mb-1">Ghi chú (tuỳ chọn)</label>
        <textarea
          className="w-full h-24 px-3 py-2 rounded border border-input bg-background text-sm"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Ý kiến khi ký…"
        />
        {/*
          Trước đây câu này ghi thẳng tên bảng cơ sở dữ liệu (text_process) ra màn
          hình người dùng. Người dùng văn phòng không biết đó là gì, và cũng không
          nên biết — đã đổi sang câu mô tả việc sẽ xảy ra.
        */}
        <div className="text-xs text-muted-foreground mt-2">
          Sau khi xác nhận, văn bản sẽ được chuyển sang người ký cấp kế tiếp. Nếu bạn là người ký
          cuối cùng, văn bản chuyển sang trạng thái chờ ban hành.
        </div>
      </Modal>

      <Modal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title="Từ chối văn bản"
        footer={
          <>
            <Button variant="outline" size="md" onClick={() => setRejectOpen(false)}>
              Huỷ
            </Button>
            <Button
              variant="destructive"
              size="md"
              onClick={onReject}
              disabled={loading === "reject" || !rejectReason}
            >
              {loading === "reject" ? "Đang xử lý…" : "Xác nhận từ chối"}
            </Button>
          </>
        }
      >
        <label className="block text-sm font-medium mb-1">
          Lý do từ chối <span className="text-destructive">*</span>
        </label>
        <textarea
          className="w-full h-24 px-3 py-2 rounded border border-input bg-background text-sm"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Nêu rõ lý do…"
          required
        />
      </Modal>

      <Modal
        open={forwardOpen}
        onClose={() => setForwardOpen(false)}
        title="Uỷ quyền ký"
        size="lg"
        footer={
          <>
            <Button variant="outline" size="md" onClick={() => setForwardOpen(false)}>
              Huỷ
            </Button>
            <Button
              variant="default"
              size="md"
              onClick={onForward}
              disabled={loading === "forward" || !forwardPick}
            >
              {loading === "forward" ? "Đang gửi…" : `Uỷ quyền cho ${forwardPick?.fullName ?? "…"}`}
            </Button>
          </>
        }
      >
        <div className="flex items-center gap-2 mb-3">
          <input
            className="flex-1 h-9 px-3 rounded border border-input bg-background text-sm"
            placeholder="Tên hoặc mã nhân viên…"
            value={forwardSearch}
            onChange={(e) => setForwardSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadForwardSigners()}
          />
          <Button variant="outline" size="md" onClick={loadForwardSigners}>
            Tìm
          </Button>
        </div>
        <div className="max-h-64 overflow-y-auto rounded border border-border">
          {signers.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              Nhập từ khoá rồi bấm "Tìm"
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {signers.map((s) => (
                <li key={s.staffId}>
                  <button
                    type="button"
                    onClick={() =>
                      setForwardPick({ staffId: s.staffId, fullName: s.fullName ?? s.loginName })
                    }
                    className={`w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between ${forwardPick?.staffId === s.staffId ? "bg-primary-50" : ""}`}
                  >
                    <div>
                      <div className="text-sm font-medium">{s.fullName ?? s.loginName}</div>
                      <div className="text-xs text-muted-foreground">{s.loginName}</div>
                    </div>
                    {forwardPick?.staffId === s.staffId && (
                      <Check className="h-4 w-4 text-primary-600" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <label className="block text-sm font-medium mt-3 mb-1">Ghi chú uỷ quyền</label>
        <textarea
          className="w-full h-16 px-3 py-2 rounded border border-input bg-background text-sm"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Modal>

      <Modal
        open={initialOpen}
        onClose={() => setInitialOpen(false)}
        title="Chuyển ký nháy"
        size="lg"
        footer={
          <>
            <Button variant="outline" size="md" onClick={() => setInitialOpen(false)}>
              Huỷ
            </Button>
            <Button
              variant="default"
              size="md"
              onClick={onTransferInitial}
              disabled={loading === "initial" || !initialPicks.length}
            >
              {loading === "initial" ? "Đang gửi…" : `Chuyển cho ${initialPicks.length} người`}
            </Button>
          </>
        }
      >
        <div className="flex items-center gap-2 mb-3">
          <input
            className="flex-1 h-9 px-3 rounded border border-input bg-background text-sm"
            placeholder="Tên hoặc mã nhân viên…"
            value={initialSearch}
            onChange={(e) => setInitialSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadInitialSigners()}
          />
          <Button variant="outline" size="md" onClick={loadInitialSigners}>
            Tìm
          </Button>
        </div>
        <div className="max-h-64 overflow-y-auto rounded border border-border">
          {initialSigners.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              Nhập từ khoá rồi bấm "Tìm"
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {initialSigners.map((s) => (
                <li key={s.staffId}>
                  <button
                    type="button"
                    onClick={() =>
                      setInitialPicks((prev) =>
                        prev.includes(s.staffId)
                          ? prev.filter((id) => id !== s.staffId)
                          : [...prev, s.staffId],
                      )
                    }
                    className={`w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between ${initialPicks.includes(s.staffId) ? "bg-primary-50" : ""}`}
                  >
                    <div>
                      <div className="text-sm font-medium">{s.fullName ?? s.loginName}</div>
                      <div className="text-xs text-muted-foreground">{s.loginName}</div>
                    </div>
                    {initialPicks.includes(s.staffId) && (
                      <Check className="h-4 w-4 text-primary-600" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <label className="block text-sm font-medium mt-3 mb-1">Ghi chú (tuỳ chọn)</label>
        <textarea
          className="w-full h-16 px-3 py-2 rounded border border-input bg-background text-sm"
          value={initialComment}
          onChange={(e) => setInitialComment(e.target.value)}
        />
      </Modal>

      <Modal
        open={publishOpen}
        onClose={() => {
          setPublishOpen(false);
          setPublishError(null);
        }}
        title="Ban hành văn bản"
        footer={
          <>
            <Button
              variant="outline"
              size="md"
              onClick={() => {
                setPublishOpen(false);
                setPublishError(null);
              }}
            >
              Huỷ
            </Button>
            <Button
              variant="default"
              size="md"
              onClick={onPublish}
              disabled={loading === "publish"}
            >
              {loading === "publish" ? "Đang ban hành…" : "Xác nhận ban hành"}
            </Button>
          </>
        }
      >
        <label className="block text-sm font-medium mb-1">Số ký hiệu chính thức</label>
        <input
          className="w-full h-9 px-3 rounded border border-input bg-background text-sm"
          value={regNumber}
          onChange={(e) => setRegNumber(e.target.value)}
          placeholder="Để trống = hệ thống tự cấp số"
        />
        <p className="text-xs text-muted-foreground mt-1 mb-3">
          Số được tự cấp theo đơn vị ban hành. Có thể sửa tay nếu cần (hệ thống kiểm tra trùng).
        </p>
        {publishError && (
          <p className="text-sm text-destructive mb-3" role="alert">
            {publishError}
          </p>
        )}
        <label className="block text-sm font-medium mb-1">
          Danh sách staff ID nhận (phân cách bằng dấu phẩy)
        </label>
        <input
          className="w-full h-9 px-3 rounded border border-input bg-background text-sm"
          value={personalReceivers}
          onChange={(e) => setPersonalReceivers(e.target.value)}
          placeholder="6076, 5622, 6510"
        />
        <div className="text-xs text-muted-foreground mt-2">
          Tối giản — GĐ tiếp theo sẽ có picker chọn nhân sự trực quan.
        </div>
      </Modal>
    </>
  );
}
